import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import LoadingComponent from "../LoadingComponent";
import QuestionRenderer, { MultiPartCard } from "../questions/QuestionRenderer";
import { gradeProbabilisticResponse, isProbabilistic } from "../../utils/probabilisticGrading";
import { normalizeMath, evalNumber, shuffleArray } from "../../utils/answerMatching";
import QuestionReportButton from "../questions/QuestionReportButton";
import QuestionFilter from "../questions/QuestionFilter";
import ProbabilisticCriteriaList from "../questions/ProbabilisticCriteriaList";
import MathRenderer from "../MathRenderer";
import { API_BASE_URL } from "../../config";

const createEmptyPersistedState = () => ({
  selectedById: {},
  feedbackById: {},
  showHintById: {},
  usedHelpById: {},
  committedById: {},
  lockedById: {},
  resultById: {},
  shuffledOptionsById: {},
});

const getQuestionGroupKey = (q) => {
  const parent = q?.parent_question_number;
  if (!parent) return null;

  const scope =
    q?.doc_external_id ??
    q?.document_external_id ??
    q?.subject_module_id ??
    q?.subject_id ??
    q?.year ??
    "default";

  return `${scope}::${parent}`;
};

const buildQuestionGroups = (questions) => {
  const groups = [];
  const parentMap = {};

  questions.forEach((q) => {
    const parent = q.parent_question_number;
    const groupKey = getQuestionGroupKey(q);

    if (parent && groupKey) {
      if (parentMap[groupKey] !== undefined) {
        groups[parentMap[groupKey]].push(q);
      } else {
        parentMap[groupKey] = groups.length;
        groups.push([q]);
      }
    } else {
      groups.push([q]);
    }
  });

  return groups;
};

// Shuffle at the group level so sub-parts always stay together and consecutive.
const shuffleByGroups = (questions) => {
  return shuffleArray(buildQuestionGroups(questions)).flat();
};

const takeRandomSubsetByGroups = (questions, limit) => {
  if (!limit || limit <= 0) return questions;

  const shuffledGroups = shuffleArray(buildQuestionGroups(questions));
  const selectedGroups = [];
  let totalQuestions = 0;

  // Skip a group that will not fit rather than stopping — a large multi-part question
  // early in the shuffle used to end the loop, so a quiz offered as "25 questions" could
  // hand back two. Keep looking for groups that still fit.
  for (const group of shuffledGroups) {
    if (totalQuestions >= limit) break;
    if (selectedGroups.length > 0 && totalQuestions + group.length > limit) continue;

    selectedGroups.push(group);
    totalQuestions += group.length;
  }

  return selectedGroups.flat();
};

const Chevron = ({ dir = "right" }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
    {dir === "left" ? (
      <path
        fillRule="evenodd"
        d="M12.78 15.53a.75.75 0 0 1-1.06 0L6.69 10.5a.75.75 0 0 1 0-1.06l5.03-5.03a.75.75 0 1 1 1.06 1.06L8.28 10l4.5 4.47a.75.75 0 0 1 0 1.06z"
        clipRule="evenodd"
      />
    ) : (
      <path
        fillRule="evenodd"
        d="M7.22 4.47a.75.75 0 0 1 1.06 0l5.03 5.03a.75.75 0 0 1 0 1.06l-5.03 5.03a.75.75 0 1 1-1.06-1.06L11.72 10l-4.5-4.47a.75.75 0 0 1 0-1.06z"
        clipRule="evenodd"
      />
    )}
  </svg>
);

// track horizontal overflow for dots (ONLY)
const useHorizontalOverflow = (ref, deps = []) => {
  const [state, setState] = useState({
    overflow: false,
    canLeft: false,
    canRight: false,
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const calc = () => {
      const overflow = el.scrollWidth > el.clientWidth + 1;
      const canLeft = overflow && el.scrollLeft > 1;
      const canRight =
        overflow && el.scrollLeft + el.clientWidth < el.scrollWidth - 1;

      setState({ overflow, canLeft, canRight });
    };

    calc();

    const onScroll = () => calc();
    el.addEventListener("scroll", onScroll, { passive: true });

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => calc());
      ro.observe(el);
    }

    window.addEventListener("resize", calc);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", calc);
      if (ro) ro.disconnect();
    };
  }, deps);

  return state;
};

const ModuleQuiz = ({ questions, subjectModuleId, moduleNumber, randomLimit = 0, mode = null }) => {
  // currentIndex points to the first flat question index of the current card.
  // Navigation jumps by group (card), not by individual question.
  const [currentIndex, setCurrentIndex] = useState(0);

  // Supports free navigation without losing state
  const [selectedByIdx, setSelectedByIdx] = useState([]);
  const [feedbackByIdx, setFeedbackByIdx] = useState([]);
  const [showHintByIdx, setShowHintByIdx] = useState([]);
  const [usedHelpByIdx, setUsedHelpByIdx] = useState([]);
  // AI marking state for open-ended questions, keyed by flat index.
  const [gradingByIdx, setGradingByIdx] = useState({});
  const [committedCheckByIdx, setCommittedCheckByIdx] = useState([]);
  const [lockedByIdx, setLockedByIdx] = useState([]);

  // committed outcomes only: "correct" | "correct_with_help" | "incorrect" | "skipped" | null
  const [results, setResults] = useState([]);
  const [showSummary, setShowSummary] = useState(false);

  const [allQuestions, setAllQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);

  const [activeQuestions, setActiveQuestions] = useState([]);
  const [activeShuffledOptions, setActiveShuffledOptions] = useState([]);

  const [filter, setFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);

  const [soundEnabled, setSoundEnabled] = useState(
    () => localStorage.getItem("quiz_sound") !== "off"
  );

  const persistedStateRef = useRef(createEmptyPersistedState());

  const toggleSound = () => {
    setSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem("quiz_sound", next ? "on" : "off");
      return next;
    });
  };

  const playCorrectSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const notes = [523.25, 659.25]; // C5, E5
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.12);
        gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.12);
        gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + i * 0.12 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.12 + 0.35);
        osc.start(ctx.currentTime + i * 0.12);
        osc.stop(ctx.currentTime + i * 0.12 + 0.35);
      });
    } catch (e) {
      // Silently fail if audio context not available
    }
  };

  const studentInfo = JSON.parse(localStorage.getItem("user") || "{}");
  const studentId = studentInfo?.id;

  const { exam_type: url_exam_type, subject: url_subject, module: url_module } = useParams();
  const navigate = useNavigate();

  const effectiveSubjectModuleId =
    subjectModuleId ?? questions?.[0]?.subject_module_id;

  const clearPersistedState = () => {
    persistedStateRef.current = createEmptyPersistedState();
  };

  const resetQuizForQuestions = (effectiveQuestions, { preserveState = true } = {}) => {
    const persisted = preserveState ? persistedStateRef.current : createEmptyPersistedState();

    if (!preserveState) {
      persistedStateRef.current = createEmptyPersistedState();
    }

    setActiveQuestions(effectiveQuestions);
    setCurrentIndex(0);
    setShowSummary(false);

    setResults(
      effectiveQuestions.map((q) => persisted.resultById[q.id] ?? null)
    );
    setSelectedByIdx(
      effectiveQuestions.map((q) => persisted.selectedById[q.id] ?? null)
    );
    setFeedbackByIdx(
      effectiveQuestions.map((q) => persisted.feedbackById[q.id] ?? null)
    );
    setShowHintByIdx(
      effectiveQuestions.map((q) => persisted.showHintById[q.id] ?? false)
    );
    setUsedHelpByIdx(
      effectiveQuestions.map((q) => persisted.usedHelpById[q.id] ?? false)
    );
    setCommittedCheckByIdx(
      effectiveQuestions.map((q) => persisted.committedById[q.id] ?? false)
    );
    setLockedByIdx(
      effectiveQuestions.map((q) => persisted.lockedById[q.id] ?? false)
    );

    const shuffled = effectiveQuestions.map((q) => {
      const saved = persisted.shuffledOptionsById[q.id];
      return Array.isArray(saved) && saved.length > 0
        ? saved
        : q.options
        ? shuffleArray(q.options)
        : [];
    });
    setActiveShuffledOptions(shuffled);
  };

  useEffect(() => {
    const persisted = persistedStateRef.current;

    activeQuestions.forEach((q, idx) => {
      const id = q?.id;
      if (id == null) return;

      persisted.selectedById[id] = selectedByIdx[idx] ?? null;
      persisted.feedbackById[id] = feedbackByIdx[idx] ?? null;
      persisted.showHintById[id] = showHintByIdx[idx] ?? false;
      persisted.usedHelpById[id] = usedHelpByIdx[idx] ?? false;
      persisted.committedById[id] = committedCheckByIdx[idx] ?? false;
      persisted.lockedById[id] = lockedByIdx[idx] ?? false;
      persisted.resultById[id] = results[idx] ?? null;

      if (Array.isArray(activeShuffledOptions[idx]) && activeShuffledOptions[idx].length > 0) {
        persisted.shuffledOptionsById[id] = activeShuffledOptions[idx];
      }
    });
  }, [
    activeQuestions,
    selectedByIdx,
    feedbackByIdx,
    showHintByIdx,
    usedHelpByIdx,
    committedCheckByIdx,
    lockedByIdx,
    results,
    activeShuffledOptions,
  ]);

  // ── Group questions by parent_question_number ───────────────────────────────
  // Each group is either a single standalone question or a set of sub-parts that
  // share a parent (e.g. Q3a, Q3b_i, Q3b_ii all have parent_question_number "3").
  // Sub-parts always appear consecutive in activeQuestions (ensured by shuffleByGroups).
  const groups = useMemo(() => {
    const result = [];
    const parentMap = {};

    activeQuestions.forEach((q, idx) => {
      const parent = q.parent_question_number;
      const groupKey = getQuestionGroupKey(q);

      if (parent && groupKey) {
        if (parentMap[groupKey] !== undefined) {
          result[parentMap[groupKey]].indices.push(idx);
        } else {
          parentMap[groupKey] = result.length;
          result.push({ parent, groupKey, indices: [idx], isMultiPart: true });
        }
      } else {
        result.push({ parent: null, groupKey: null, indices: [idx], isMultiPart: false });
      }
    });

    // Sort sub-parts within each group by question_number so (a) always comes before (b)(i) etc.
    result.forEach((g) => {
      if (g.isMultiPart) {
        g.indices.sort((a, b) =>
          (activeQuestions[a].question_number ?? '').localeCompare(
            activeQuestions[b].question_number ?? '', undefined, { numeric: true }
          )
        );
      }
    });

    return result;
  }, [activeQuestions]);

  // The group that contains currentIndex (first index of that group)
  const currentGroupIdx = useMemo(
    () => Math.max(0, groups.findIndex((g) => g.indices.includes(currentIndex))),
    [groups, currentIndex]
  );
  const currentGroup = groups[currentGroupIdx] ?? { parent: null, indices: [currentIndex], isMultiPart: false };

  const dotsRef = useRef(null);
  const dotRefs = useRef([]);

  const dotsScroll = useHorizontalOverflow(dotsRef, [
    groups.length,
    results,
    currentGroupIdx,
  ]);

  const scrollDotsBy = (dir) => {
    const el = dotsRef.current;
    if (!el) return;
    const amount = Math.max(180, el.clientWidth * 0.8);
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };


  useEffect(() => {
    const dotEl = dotRefs.current[currentGroupIdx];
    if (!dotEl) return;
    dotEl.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [currentGroupIdx, groups.length]);

  useEffect(() => {
    let cancelled = false;

    const applyQuickPractice = (arr) => {
      if (!randomLimit || randomLimit <= 0) return arr;
      return takeRandomSubsetByGroups(arr, randomLimit);
    };

    const run = async () => {
      if (!questions || questions.length === 0) {
        if (!cancelled) {
          clearPersistedState();
          setAllQuestions([]);
          resetQuizForQuestions([], { preserveState: false });
          setLoadingQuestions(false);
        }
        return;
      }

      if (!studentId || !effectiveSubjectModuleId) {
        let randomized = shuffleByGroups(questions);
        randomized = mode === "quick" && randomLimit > 0
          ? applyQuickPractice(randomized)
          : randomized;

        if (!cancelled) {
          const normalized = randomized.map((q) => ({
            ...q,
            quiz_status: q.quiz_status || "not_started",
          }));
          clearPersistedState();
          setAllQuestions(normalized);
          setFilter("all");
          resetQuizForQuestions(normalized, { preserveState: false });
          setLoadingQuestions(false);
        }
        return;
      }

      try {
        setLoadingQuestions(true);

        const response = await axios.get(
          `${API_BASE_URL}/api/ordered_module_quiz_questions`,
          {
            params: {
              student_id: studentId,
              subject_module_id: effectiveSubjectModuleId,
            },
          }
        );

        const ordered = response.data || [];
        let effective =
          ordered.length > 0
            ? ordered
            : shuffleByGroups(questions).map((q) => ({
                ...q,
                quiz_status: q.quiz_status || "not_started",
              }));
      
        if (mode === "quick" && randomLimit > 0) {
          effective = applyQuickPractice(effective);
        }

        if (!cancelled) {
          clearPersistedState();
          setAllQuestions(effective);
          setFilter("all");
          resetQuizForQuestions(effective, { preserveState: false });
        }
      } catch (error) {
        console.error("Error fetching ordered module quiz questions:", error);

        let fallback = shuffleByGroups(questions).map((q) => ({
          ...q,
          quiz_status: q.quiz_status || "not_started",
        }));

        if (mode === "quick" && randomLimit > 0) {
          fallback = applyQuickPractice(fallback);
        }

        if (!cancelled) {
          clearPersistedState();
          setAllQuestions(fallback);
          setFilter("all");
          resetQuizForQuestions(fallback, { preserveState: false });
        }
      } finally {
        if (!cancelled) setLoadingQuestions(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [questions, studentId, effectiveSubjectModuleId, mode, randomLimit]);

  const statusCounts = useMemo(() => {
    const counts = {
      all: allQuestions.length,
      not_started: 0,
      in_progress: 0,
      skipped: 0,
      mastered: 0,
    };

    allQuestions.forEach((q) => {
      const s = q.quiz_status || "not_started";
      if (counts[s] != null) counts[s] += 1;
    });

    return counts;
  }, [allQuestions]);

  const shouldShowFilters = useMemo(() => {
    if (!allQuestions.length) return false;

    return (
      statusCounts.in_progress > 0 ||
      statusCounts.skipped > 0 ||
      statusCounts.mastered > 0
    );
  }, [allQuestions.length, statusCounts]);

  const availableFilterKeys = useMemo(() => {
    if (!shouldShowFilters) return [];
    const keys = ["all"];
    ["not_started", "in_progress", "skipped", "mastered"].forEach((k) => {
      if (statusCounts[k] > 0) keys.push(k);
    });
    return keys;
  }, [shouldShowFilters, statusCounts]);

  const applyFilter = (nextFilter) => {
    setFilter(nextFilter);

    const nextQuestions =
      nextFilter === "all"
        ? allQuestions
        : allQuestions.filter(
            (q) => (q.quiz_status || "not_started") === nextFilter
          );

    if (!nextQuestions.length) {
      setFilter("all");
      resetQuizForQuestions(allQuestions);
      return;
    }

    resetQuizForQuestions(nextQuestions);
  };

  useEffect(() => {
    if (!showSummary) return;

    navigate(`/${url_exam_type}/${url_subject}/${url_module}/learn-summary`, {
      state: {
        questions: activeQuestions,
        results,
        // Outcome strings alone can't be scored: "partial" doesn't say how many of the
        // marks were earned. The verdicts travel with them so the summary scores marks.
        verdicts: activeQuestions.map((_, idx) => {
          const graded = gradingByIdx[idx];
          return graded?.state === "graded"
            ? {
                state: "graded",
                marksAwarded: graded.marksAwarded,
                maxMarks: graded.maxMarks,
              }
            : null;
        }),
        subjectModuleId: effectiveSubjectModuleId,
        subject_module_id: effectiveSubjectModuleId,
        moduleNumber,
        module_number: moduleNumber,
        filter_used: filter,
      },
    });
  }, [
    showSummary,
    navigate,
    url_exam_type,
    url_subject,
    url_module,
    activeQuestions,
    results,
    gradingByIdx,
    effectiveSubjectModuleId,
    moduleNumber,
    filter,
  ]);

  if (loadingQuestions) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <LoadingComponent />
      </div>
    );
  }

  if (!activeQuestions || activeQuestions.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-gray-500 text-sm">
          No questions available for this module yet.
        </p>
      </div>
    );
  }
  

  const question = activeQuestions[currentIndex];
  const optionsForCurrent =
    activeShuffledOptions[currentIndex] &&
    activeShuffledOptions[currentIndex].length > 0
      ? activeShuffledOptions[currentIndex]
      : question.options || [];

  const getCorrectAnswer = (q) => {
    if (!q.answers || q.answers.length === 0) return null;
    return q.answers.find((a) => a.is_correct) || q.answers[0];
  };

  const correctAnswerObj = getCorrectAnswer(question);
  const correctAnswerText = correctAnswerObj?.content;
  const correctExplanation = correctAnswerObj?.explanation;

  const selectedOption = selectedByIdx[currentIndex] ?? null;
  const feedback = feedbackByIdx[currentIndex] ?? null;
  const isHintOpen = showHintByIdx[currentIndex] ?? false;
  const usedHelp = usedHelpByIdx[currentIndex] ?? false;
  const hasCommitted = committedCheckByIdx[currentIndex] ?? false;
  const isLocked = lockedByIdx[currentIndex] ?? false;

  const hintButtonLabel = isHintOpen ? "Hide Explanation" : "Show Explanation";
  const primaryButtonLabel = "Check";

  const primaryDisabled = (() => {
    if (hasCommitted || isLocked) return true;
    const qType = question?.question_type;
    // Open-ended questions have no answer text to compare against — the mark scheme
    // stays on the server — so only require that the student has written something.
    if (isProbabilistic(question)) {
      if (gradingByIdx[currentIndex]?.state === "grading") return true;
      return !selectedOption?.toString().trim();
    }
    if (qType === "fill_in_the_blank") {
      const blanks = question?.answers?.length ?? 0;
      const userArr = Array.isArray(selectedOption) ? selectedOption : [];
      return blanks === 0 || !userArr.slice(0, blanks).every((v) => v?.toString().trim());
    }
    return !selectedOption?.toString().trim() || !correctAnswerText;
  })();

  const goToPrevQuestion = () => {
    if (currentGroupIdx <= 0) return;
    setCurrentIndex(groups[currentGroupIdx - 1].indices[0]);
  };

  const goToNextQuestion = () => {
    if (currentGroupIdx >= groups.length - 1) {
      setShowSummary(true);
      return;
    }
    setCurrentIndex(groups[currentGroupIdx + 1].indices[0]);
  };

  const sendUserQuestionStatus = async ({ questionId, event, isCorrect = null, usedHelp = null }) => {
    // studentId only gates the call — the server takes the student from the session,
    // so we no longer send an id it would (rightly) ignore.
    if (!studentId || !questionId) return;

    try {
      await axios.post(`${API_BASE_URL}/api/create_or_update/user_question_status`, {
        question_id: questionId,
        event,
        is_correct: isCorrect,
        // Travels with the attempt rather than as its own earlier request, so the two
        // can never disagree about whether help was used.
        ...(usedHelp === null ? {} : { used_help: usedHelp }),
      });
    } catch (error) {
      console.error("Error updating user question status:", error);
    }
  };

  // ── Math answer normalisation ────────────────────────────────────────────────
  // MathLive outputs LaTeX (e.g. \frac{1}{2}, x^{2}).
  // DB stores plain text (e.g. 1/2, x^2).
  // normalizeMath converts both sides to the same plain form before comparing.
  // Evaluates a normalised string as a number, including simple fractions.
  // ── Answer grading ──────────────────────────────────────────────────────────
  // Checks user answer against ALL stored answers — supports alternative correct
  // forms (e.g. 1/4 and 0.25) and multiple tolerance/case_sensitive values.
  //
  // fill_in_the_blank uses positional grading: answers[i] maps to blank i.
  // Each blank must match its corresponding answer — never use .some() across
  // all answers for this type.
  const checkAnswer = (qType, userAnswer, answers) => {
    if (!answers?.length) return false;

    // fill_in_the_blank: positional — userAnswer is an array of strings
    if (qType === "fill_in_the_blank") {
      const userArr = Array.isArray(userAnswer) ? userAnswer : [];
      return answers.every((answerObj, i) => {
        const userVal   = normalizeMath(userArr[i] ?? "");
        const correctVal = normalizeMath(answerObj.content ?? "");
        const tol = parseFloat(answerObj?.tolerance ?? 0);
        const userNum    = evalNumber(userVal);
        const correctNum = evalNumber(correctVal);
        if (!isNaN(userNum) && !isNaN(correctNum)) {
          return Math.abs(userNum - correctNum) <= (isNaN(tol) ? 0 : tol);
        }
        if (answerObj?.case_sensitive) {
          return String(userArr[i] ?? "").trim() === String(answerObj.content ?? "").trim();
        }
        return userVal === correctVal;
      });
    }

    return answers.some((answerObj) => {
      const correctAnswer = answerObj.content;
      const user    = normalizeMath(userAnswer);
      const correct = normalizeMath(correctAnswer);

      if (qType === "numeric_input") {
        const userNum    = evalNumber(user);
        const correctNum = evalNumber(correct);
        const tol        = parseFloat(answerObj?.tolerance ?? 0);

        // Path 1 — both sides parse as numbers: numeric compare with tolerance.
        if (!isNaN(userNum) && !isNaN(correctNum)) {
          return Math.abs(userNum - correctNum) <= (isNaN(tol) ? 0 : tol);
        }
        // Path 2 — structured non-numeric (coordinate pair, ratio, list,
        // algebraic expression). Fall back to normalised string match.
        return user === correct;
      }

      if (qType === "text_input") {
        const u = String(userAnswer ?? "").trim().toLowerCase();
        const c = String(correctAnswer ?? "").trim().toLowerCase();
        return answerObj?.case_sensitive
          ? String(userAnswer ?? "").trim() === String(correctAnswer ?? "").trim()
          : u === c;
      }

      // multiple_choice — exact match on raw values
      return String(userAnswer ?? "").trim() === String(correctAnswer ?? "").trim();
    });
  };

  // General handler — works for any flat question index (single or sub-part).
  const handlePrimaryForIndex = async (idx) => {
    const question = activeQuestions[idx];
    if (!question) return;

    const selectedOption    = selectedByIdx[idx] ?? null;
    const questionLocked    = lockedByIdx[idx] ?? false;
    const questionCommitted = committedCheckByIdx[idx] ?? false;
    const questionUsedHelp  = usedHelpByIdx[idx] ?? false;

    if (questionLocked || questionCommitted) return;

    // ── Open-ended: marked by AI on the server ───────────────────────────────
    // The mark scheme never reaches the browser, so there is nothing to check here.
    if (isProbabilistic(question)) {
      const answerText = (selectedOption ?? "").toString();
      if (!answerText.trim()) return;

      setGradingByIdx((prev) => ({ ...prev, [idx]: { state: "grading" } }));

      // Help travels with the attempt, so a full-mark answer given after opening the
      // explanation is stored as correct_with_help — same rule as the deterministic path.
      const verdict = await gradeProbabilisticResponse({
        questionId: question.id,
        studentAnswer: answerText,
        usedHelp: questionUsedHelp,
      });

      // A grading failure is ours, not the student's: leave the question open so they
      // can retry, and never record a mark.
      if (!verdict.ok) {
        setGradingByIdx((prev) => ({
          ...prev,
          [idx]: { state: "failed", message: verdict.message, retryable: verdict.retryable },
        }));
        return;
      }

      setGradingByIdx((prev) => ({
        ...prev,
        [idx]: {
          state: "graded",
          outcome: verdict.outcome,
          marksAwarded: verdict.marksAwarded,
          maxMarks: verdict.maxMarks,
          criteria: verdict.criteria,
          feedback: verdict.feedback,
        },
      }));

      setFeedbackByIdx((prev) => {
        const copy = [...prev]; copy[idx] = verdict.outcome; return copy;
      });

      if (verdict.outcome === "correct" && soundEnabled) playCorrectSound();

      // Anything short of full marks reveals the explanation, exactly as a wrong
      // deterministic answer does. The verdict says what THIS answer was missing; the
      // explanation is the only place a full-mark answer is shown, and the moment
      // straight after a failed attempt is when it lands best.
      //
      // Revealing it after committing is not "using help" — only opening it yourself
      // before answering counts (see handleToggleHelpForIndex).
      revealExplanationForIndex(idx, verdict.outcome !== "correct");

      setCommittedCheckByIdx((prev) => { const copy = [...prev]; copy[idx] = true; return copy; });
      setLockedByIdx((prev)         => { const copy = [...prev]; copy[idx] = true; return copy; });

      // No "check" event: the grading endpoint already recorded this attempt, and
      // sending one would count it twice.
      setResults((prev) => {
        const copy = [...prev];
        copy[idx] =
          verdict.outcome === "correct"
            ? (questionUsedHelp ? "correct_with_help" : "correct")
            : verdict.outcome; // "partial" | "incorrect"
        return copy;
      });
      return;
    }

    const isCorrect = checkAnswer(question.question_type, selectedOption, question.answers);

    if (isCorrect && soundEnabled) playCorrectSound();

    setFeedbackByIdx((prev) => {
      const copy = [...prev]; copy[idx] = isCorrect ? "correct" : "incorrect"; return copy;
    });

    // Wrong answers reveal the explanation. This is the automatic path, so it records
    // nothing — the grade below is decided by questionUsedHelp, captured before any of this.
    revealExplanationForIndex(idx, !isCorrect);

    await sendUserQuestionStatus({
      questionId: question.id,
      event: "check",
      isCorrect,
      usedHelp: questionUsedHelp,
    });

    setCommittedCheckByIdx((prev) => { const copy = [...prev]; copy[idx] = true; return copy; });
    setLockedByIdx((prev)         => { const copy = [...prev]; copy[idx] = true; return copy; });

    const committedOutcome = isCorrect
      ? questionUsedHelp ? "correct_with_help" : "correct"
      : "incorrect";

    setResults((prev) => { const copy = [...prev]; copy[idx] = committedOutcome; return copy; });
  };

  // Single-question shorthand (used by the standalone card Check button)
  const handlePrimary = () => handlePrimaryForIndex(currentIndex);

  const handleSkip = async () => {
    // Skip all uncommitted sub-parts in the current group
    for (const idx of currentGroup.indices) {
      if (!(committedCheckByIdx[idx] ?? false)) {
        setResults((prev) => {
          const copy = [...prev];
          if (copy[idx] == null) copy[idx] = "skipped";
          return copy;
        });
        await sendUserQuestionStatus({
          questionId: activeQuestions[idx].id,
          event: "skip",
          isCorrect: null,
        });
      }
    }

    if (currentGroupIdx >= groups.length - 1) {
      setShowSummary(true);
      return;
    }
    setCurrentIndex(groups[currentGroupIdx + 1].indices[0]);
  };

  // The explanation opens two ways, and only one of them counts as help.
  //
  // revealExplanationForIndex is the app showing it AFTER an attempt has been graded.
  // That is feedback on work already marked, so it records nothing and can never change
  // the grade it is explaining — a wrong answer stays incorrect with the explanation open.
  //
  // handleToggleHelpForIndex is the student asking for it themselves. Asked BEFORE
  // answering, that is help, and a right answer is recorded as correct_with_help.
  //
  // Keep these separate. Routing an automatic reveal through the toggle would silently
  // mark every student who got a question wrong as having used help.
  const revealExplanationForIndex = (idx, open) => {
    setShowHintByIdx((prev) => { const copy = [...prev]; copy[idx] = open; return copy; });
  };

  const handleToggleHelpForIndex = (idx) => {
    const nextOpen = !(showHintByIdx[idx] ?? false);
    revealExplanationForIndex(idx, nextOpen);
    if (nextOpen) {
      setUsedHelpByIdx((prev) => { const copy = [...prev]; copy[idx] = true; return copy; });
    }
  };

  const handleToggleHelp = () => handleToggleHelpForIndex(currentIndex);

  // For multi-part cards: card is "done" when all sub-parts are committed
  const groupAllCommitted = currentGroup.indices.every((idx) => committedCheckByIdx[idx] ?? false);
  const showSkip = !groupAllCommitted;

  return (
    <div className="flex flex-col w-full mx-auto bg-white rounded-lg shadow-sm p-6">
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      
      <div className="mb-3">
      {/* Row 1 (MOBILE): Question counter left, Sound toggle + Report right */}
      <div className="flex items-start justify-between gap-2 sm:hidden">
        <p className="text-xs text-gray-500">
          Question {currentGroupIdx + 1} of {groups.length}
        </p>

        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={toggleSound}
            title={soundEnabled ? "Mute sound" : "Unmute sound"}
            className="flex items-center rounded-md px-2 py-1 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            {soundEnabled ? (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path d="M10 3.75a.75.75 0 0 0-1.264-.546L4.703 7H3.167a.75.75 0 0 0-.7.48A6.985 6.985 0 0 0 2 10c0 .887.165 1.737.468 2.52.111.29.39.48.7.48h1.535l4.033 3.796A.75.75 0 0 0 10 16.25V3.75ZM15.95 5.05a.75.75 0 0 0-1.06 1.061 5.5 5.5 0 0 1 0 7.778.75.75 0 0 0 1.06 1.06 7 7 0 0 0 0-9.899Z" />
                <path d="M13.829 7.172a.75.75 0 0 0-1.061 1.06 2.5 2.5 0 0 1 0 3.536.75.75 0 0 0 1.06 1.06 4 4 0 0 0 0-5.656Z" />
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path d="M9.547 3.062A.75.75 0 0 1 10 3.75v12.5a.75.75 0 0 1-1.264.546L4.703 13H3.167a.75.75 0 0 1-.7-.48A6.985 6.985 0 0 1 2 10c0-.887.165-1.736.468-2.52a.75.75 0 0 1 .7-.48h1.535l4.033-3.796a.75.75 0 0 1 .811-.142ZM13.28 7.22a.75.75 0 1 0-1.06 1.06L13.94 10l-1.72 1.72a.75.75 0 0 0 1.06 1.06L15 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L16.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L15 8.94l-1.72-1.72Z" />
              </svg>
            )}
          </button>
          <QuestionReportButton
            questionId={question.id}
            studentId={studentId}
            variant="link"
          />
        </div>
      </div>

      {/* Row 2 (MOBILE): Filter left-aligned, smaller */}
      <div className="mt-2 sm:hidden">
        <QuestionFilter
          filter={filter}
          statusCounts={statusCounts}
          availableFilterKeys={availableFilterKeys}
          shouldShow={shouldShowFilters}
          onApplyFilter={applyFilter}
          open={filterOpen}
          setOpen={setFilterOpen}
          align="left"
          widthClass="w-40"
          size="sm"
        />
      </div>

      {/* Desktop/tablet: your normal layout (counter left, controls right) */}
      <div className="hidden sm:flex sm:items-start sm:justify-between">
        <p className="text-xs text-gray-500">
          Question {currentGroupIdx + 1} of {groups.length}
        </p>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleSound}
            title={soundEnabled ? "Mute sound" : "Unmute sound"}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            {soundEnabled ? (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path d="M10 3.75a.75.75 0 0 0-1.264-.546L4.703 7H3.167a.75.75 0 0 0-.7.48A6.985 6.985 0 0 0 2 10c0 .887.165 1.737.468 2.52.111.29.39.48.7.48h1.535l4.033 3.796A.75.75 0 0 0 10 16.25V3.75ZM15.95 5.05a.75.75 0 0 0-1.06 1.061 5.5 5.5 0 0 1 0 7.778.75.75 0 0 0 1.06 1.06 7 7 0 0 0 0-9.899Z" />
                <path d="M13.829 7.172a.75.75 0 0 0-1.061 1.06 2.5 2.5 0 0 1 0 3.536.75.75 0 0 0 1.06 1.06 4 4 0 0 0 0-5.656Z" />
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path d="M9.547 3.062A.75.75 0 0 1 10 3.75v12.5a.75.75 0 0 1-1.264.546L4.703 13H3.167a.75.75 0 0 1-.7-.48A6.985 6.985 0 0 1 2 10c0-.887.165-1.736.468-2.52a.75.75 0 0 1 .7-.48h1.535l4.033-3.796a.75.75 0 0 1 .811-.142ZM13.28 7.22a.75.75 0 1 0-1.06 1.06L13.94 10l-1.72 1.72a.75.75 0 0 0 1.06 1.06L15 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L16.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L15 8.94l-1.72-1.72Z" />
              </svg>
            )}
          </button>

          <QuestionFilter
            filter={filter}
            statusCounts={statusCounts}
            availableFilterKeys={availableFilterKeys}
            shouldShow={shouldShowFilters}
            onApplyFilter={applyFilter}
            open={filterOpen}
            setOpen={setFilterOpen}
            align="right"
            widthClass="w-56"
          />

          <QuestionReportButton questionId={question.id} studentId={studentId} />
        </div>
      </div>
    </div>

    <div className="mb-4">
      {currentGroup.isMultiPart ? (
        <MultiPartCard
          questions={currentGroup.indices.map((i) => activeQuestions[i])}
          indices={currentGroup.indices}
          parentNumber={currentGroup.parent}
          selectedByIdx={selectedByIdx}
          feedbackByIdx={feedbackByIdx}
          committedCheckByIdx={committedCheckByIdx}
          lockedByIdx={lockedByIdx}
          showHintByIdx={showHintByIdx}
          shuffledOptionsByFlatIdx={Object.fromEntries(
            currentGroup.indices.map((i) => [
              i,
              activeShuffledOptions[i]?.length ? activeShuffledOptions[i] : activeQuestions[i]?.options ?? [],
            ])
          )}
          onCheck={(idx) => handlePrimaryForIndex(idx)}
          onInputChange={(idx, val) => {
            setSelectedByIdx((prev) => { const copy = [...prev]; copy[idx] = val; return copy; });
          }}
          onFillBlankChange={(idx, blankIdx, val) => {
            setSelectedByIdx((prev) => {
              const copy = [...prev];
              const arr = Array.isArray(copy[idx]) ? [...copy[idx]] : [];
              arr[blankIdx] = val;
              copy[idx] = arr;
              return copy;
            });
          }}
          onToggleHelp={(idx) => handleToggleHelpForIndex(idx)}
          gradingByIdx={gradingByIdx}
          onRetryGrade={(idx) => handlePrimaryForIndex(idx)}
        />
      ) : (
        <QuestionRenderer
          question={question}
          selectedOption={selectedByIdx[currentIndex] ?? null}
          feedback={feedbackByIdx[currentIndex] ?? null}
          hasCommitted={committedCheckByIdx[currentIndex] ?? false}
          isLocked={lockedByIdx[currentIndex] ?? false}
          correctAnswerText={correctAnswerText}
          shuffledOptions={optionsForCurrent}
          onOptionClick={(opt) => {
            setSelectedByIdx((prev) => {
              const copy = [...prev];
              copy[currentIndex] = opt;
              return copy;
            });
            if (feedbackByIdx[currentIndex] === "incorrect") {
              setFeedbackByIdx((prev) => {
                const copy = [...prev];
                copy[currentIndex] = null;
                return copy;
              });
            }
          }}
          onInputChange={(val) => {
            setSelectedByIdx((prev) => {
              const copy = [...prev];
              copy[currentIndex] = val;
              return copy;
            });
          }}
          onFillBlankChange={(i, val) => {
            setSelectedByIdx((prev) => {
              const copy = [...prev];
              const arr = Array.isArray(copy[currentIndex]) ? [...copy[currentIndex]] : [];
              arr[i] = val;
              copy[currentIndex] = arr;
              return copy;
            });
          }}
          onInputSubmit={handlePrimary}
          grading={gradingByIdx[currentIndex]}
          onRetryGrade={() => handlePrimaryForIndex(currentIndex)}
        />
      )}
    </div>

      {/* ── Explanation block (single-question cards only) ── */}
      {!currentGroup.isMultiPart && isHintOpen && correctExplanation && (
        <div className={`mb-3 rounded-xl border px-4 py-3 text-sm leading-relaxed ${
          feedback === "correct"
            ? "bg-emerald-50 border-emerald-200 text-emerald-900"
            : feedback === "incorrect"
            ? "bg-amber-50 border-amber-200 text-amber-900"
            : "bg-slate-50 border-slate-200 text-slate-700"
        }`}>
          <div className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider mb-1.5 ${
            feedback === "correct"    ? "text-emerald-600"
            : feedback === "incorrect" ? "text-amber-600"
            : "text-slate-400"
          }`}>
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
              <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
            </svg>
            {isProbabilistic(question)
              ? "Extra explanation"
              : feedback === "incorrect"
              ? "Not quite right — here's why"
              : "Explanation"}
          </div>
          <p className="whitespace-pre-line"><MathRenderer content={correctExplanation} /></p>
          <ProbabilisticCriteriaList criteria={gradingByIdx[currentIndex]?.criteria} />
        </div>
      )}

      {/* Actions: NOT scrollable. Always one row. Hint link above row (single-q only) */}
      <div className="mt-4">
        {!currentGroup.isMultiPart && (
          <button
            type="button"
            onClick={handleToggleHelp}
            className="text-xs font-semibold text-[#1896b9] hover:text-[#064759] underline"
          >
            {hintButtonLabel}
          </button>
        )}

        <div className="mt-3 flex w-full flex-nowrap items-stretch gap-2">
          <button
            type="button"
            onClick={goToPrevQuestion}
            disabled={currentGroupIdx === 0}
            className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition whitespace-nowrap ${
              currentGroupIdx === 0
                ? "border-gray-200 text-gray-400 cursor-not-allowed bg-white"
                : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
            }`}
          >
            Back
          </button>

          {showSkip && (
            <button
              type="button"
              onClick={handleSkip}
              className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition whitespace-nowrap"
            >
              Skip
            </button>
          )}

          {groupAllCommitted || currentGroup.isMultiPart && groupAllCommitted ? (
            <button
              type="button"
              onClick={goToNextQuestion}
              className="flex-[1.15] rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 transition whitespace-nowrap"
            >
              Next
            </button>
          ) : currentGroup.isMultiPart ? (
            // Multi-part: Next is disabled until all sub-parts are checked
            <button
              type="button"
              disabled
              className="flex-[1.15] rounded-md px-4 py-2 text-sm font-semibold text-white bg-[#669999] cursor-not-allowed transition whitespace-nowrap"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handlePrimary}
              disabled={primaryDisabled}
              className={`flex-[1.15] rounded-md px-4 py-2 text-sm font-semibold text-white transition whitespace-nowrap
                ${
                  primaryDisabled
                    ? "bg-[#669999] cursor-not-allowed"
                    : "bg-emerald-600 hover:bg-emerald-700"
                }`}
            >
              {primaryButtonLabel}
            </button>
          )}
        </div>
      </div>

      {/* Progress dots: ONE line, arrows show only on overflow */}
      <div className="mt-6">
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2">
          {dotsScroll.overflow ? (
            <button
              type="button"
              onClick={() => scrollDotsBy(-1)}
              disabled={!dotsScroll.canLeft}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white transition ${
                dotsScroll.canLeft
                  ? "border-gray-200 text-gray-600 hover:bg-gray-50"
                  : "border-gray-100 text-gray-300 cursor-not-allowed"
              }`}
              aria-label="Scroll progress left"
              title="More"
            >
              <Chevron dir="left" />
            </button>
          ) : (
            <span className="block h-8 w-8" aria-hidden="true" />
          )}

          <div ref={dotsRef} className="no-scrollbar overflow-x-auto">
            <div className="flex flex-nowrap justify-center gap-1 px-2 py-2 min-w-max">
              {groups.map((group, gIdx) => {
                // For multi-part: any incorrect → red, all correct → green, any skipped → gray, else white
                const groupResults = group.indices.map((i) => results[i]);
                const hasPartial   = groupResults.some((r) => r === "partial");
                const hasIncorrect = groupResults.some((r) => r === "incorrect");
                const hasSkipped   = groupResults.some((r) => r === "skipped");
                const allCorrect   = groupResults.every((r) => r === "correct" || r === "correct_with_help");

                let bgClass = "bg-white";
                let borderClass = "border-gray-300";

                if (allCorrect) {
                  bgClass = "bg-emerald-500";
                  borderClass = "border-emerald-500";
                } else if (hasIncorrect) {
                  bgClass = "bg-red-500";
                  borderClass = "border-red-500";
                } else if (hasPartial) {
                  bgClass = "bg-amber-400";
                  borderClass = "border-amber-400";
                } else if (hasSkipped) {
                  bgClass = "bg-gray-300";
                  borderClass = "border-gray-300";
                }

                const isCurrent = gIdx === currentGroupIdx;
                const ringClass = isCurrent ? " ring-2 ring-offset-2 ring-sky-400" : "";

                return (
                  <span
                    key={gIdx}
                    ref={(el) => (dotRefs.current[gIdx] = el)}
                    aria-hidden="true"
                    className={`w-3 h-3 rounded-full border ${bgClass} ${borderClass}${ringClass}`}
                    title={`Question ${gIdx + 1}`}
                  />
                );
              })}
            </div>
          </div>

          {dotsScroll.overflow ? (
            <button
              type="button"
              onClick={() => scrollDotsBy(1)}
              disabled={!dotsScroll.canRight}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white transition ${
                dotsScroll.canRight
                  ? "border-gray-200 text-gray-600 hover:bg-gray-50"
                  : "border-gray-100 text-gray-300 cursor-not-allowed"
              }`}
              aria-label="Scroll progress right"
              title="More"
            >
              <Chevron dir="right" />
            </button>
          ) : (
            <span className="block h-8 w-8" aria-hidden="true" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ModuleQuiz;